/**
 * AiPreviewModal — Preview AI enrichment using the REAL TaskDetail component.
 *
 * The caller creates a temporary task in Dexie BEFORE opening the modal
 * (via `createAiPreviewTask`), then passes the taskId. This guarantees
 * pixel-identical rendering with zero delay on open.
 *
 * On close, the temp task + children are deleted.
 */

import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { db, ObjectStatus } from "@/db/schema"
import { createTaskDefaults } from "@/shared/types/task"
import { toMsTimestamp } from "@totodo/operations"
import {
  setTaskDoc,
  addChecklistItem,
  addChecklistHeading,
} from "@/db/operations"
import { useLiveQueryImmediate } from "@/db/hooks"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { TaskDetail } from "@/features/task-detail/TaskDetail"

// ---------------------------------------------------------------------------
// Types (mirror server's EnrichmentResult)
// ---------------------------------------------------------------------------

export interface PreviewChecklistEntry {
  title: string
  isHeading?: boolean
}

export interface AiPreviewResult {
  title?: string
  bodyHtml?: string
  checklistItems?: PreviewChecklistEntry[]
  tags?: string[]
  deadline?: string
  userStatus?: string
  tokensUsed: number
}

interface AiPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  taskId: string | null
}

export const PREVIEW_PARENT_PATH = "root/#ai-preview"

// ---------------------------------------------------------------------------
// Helper: create temp task from AI result (call BEFORE opening modal)
// ---------------------------------------------------------------------------

export async function createAiPreviewTask(
  result: AiPreviewResult,
  fallbackTitle: string,
  templateParentPath?: string,
): Promise<string> {
  // Clean up any leftover preview tasks
  const existing = await db.totodo
    .where("createdBy")
    .equals("ai-preview")
    .toArray()
  if (existing.length > 0) {
    const childIds: string[] = []
    for (const t of existing) {
      const children = await db.totodo
        .where("parentPath")
        .startsWith(`${t.parentPath}/${t.id}`)
        .primaryKeys()
      childIds.push(...(children as string[]))
    }
    await db.totodo.bulkDelete([
      ...existing.map((r) => r.id!),
      ...childIds,
    ])
  }

  // Create the temp task
  const taskId = crypto.randomUUID()
  const now = new Date().toISOString()
  await db.totodo.add(
    createTaskDefaults({
      id: taskId,
      title: result.title || fallbackTitle,
      parentPath: templateParentPath || PREVIEW_PARENT_PATH,
      status: ObjectStatus.Unfinished,
      tags: result.tags ?? [],
      dueAt: toMsTimestamp(result.deadline),
      userStatus: result.userStatus,
      createdAt: now,
      createdBy: "ai-preview",
    }),
  )

  // Set body (Y.Doc + docHtml) via operations
  if (result.bodyHtml) {
    await setTaskDoc(taskId, result.bodyHtml, "html")
  }

  // Add checklist items/headings via operations with explicit sequential index
  if (result.checklistItems?.length) {
    for (let i = 0; i < result.checklistItems.length; i++) {
      const item = result.checklistItems[i]!
      // Pass afterIndex = i-1 so each item gets index i (0, 1, 2, ...)
      const afterIdx = i === 0 ? undefined : i - 1
      if (item.isHeading) {
        await addChecklistHeading(taskId, templateParentPath || PREVIEW_PARENT_PATH, item.title, afterIdx)
      } else {
        await addChecklistItem(taskId, templateParentPath || PREVIEW_PARENT_PATH, item.title, afterIdx)
      }
    }
  }

  return taskId
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AiPreviewModal = memo(function AiPreviewModal({
  open,
  onOpenChange,
  taskId,
}: AiPreviewModalProps) {
  const { t } = useTranslation()

  // Live query keeps the preview task reactive
  const [previewTask] = useLiveQueryImmediate(
    () => taskId ? db.totodo.get(taskId) : undefined,
    [taskId],
    undefined,
  )

  // Cleanup: delete temp task + children when modal closes
  const handleClose = useCallback(async () => {
    onOpenChange(false)
    // Give animation time to finish before deleting
    setTimeout(async () => {
      // Find preview tasks by marker
      const previewTasks = await db.totodo
        .where("createdBy")
        .equals("ai-preview")
        .toArray()
      if (previewTasks.length > 0) {
        // Also find children (checklist items) via parentPath containing the task id
        const childIds: string[] = []
        for (const t of previewTasks) {
          const children = await db.totodo
            .where("parentPath")
            .startsWith(`${t.parentPath}/${t.id}`)
            .primaryKeys()
          childIds.push(...(children as string[]))
        }
        await db.totodo.bulkDelete([
          ...previewTasks.map((r) => r.id!),
          ...childIds,
        ])
      }
    }, 300)
  }, [onOpenChange])

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={(v) => { if (!v) handleClose() }}
      title={t("recurrence.aiPreviewTitle", "AI Preview")}
      className="sm:max-w-2xl"
    >
      {previewTask ? (
        <div className="flex-1 overflow-y-auto min-h-0 relative pb-[env(safe-area-inset-bottom)]">
          {/* Interaction blocker — allows scroll but prevents clicks/focus */}
          <div className="absolute inset-0 z-10" />
          <TaskDetail
            task={previewTask}
            editTitle={false}
            showContainer
            onComplete={() => {}}
            onUncomplete={() => {}}
            onClose={handleClose}
          />
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-8">
          <span className="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      )}
    </ResponsiveSheet>
  )
})
